/**
 * Carte de suivi : la destination du client, la position du livreur quand elle
 * est connue, et le trait qui les relie.
 *
 * Les marqueurs viennent de `mapIcons` (SVG embarqués) : plus aucune requête
 * vers un domaine tiers pour afficher deux punaises.
 */

import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { customerIcon, driverIconAnimated } from './mapIcons';

interface DeliveryTrackingMapProps {
  customerLocation: [number, number]; // [lat, lng]
  driverLocation?: [number, number] | null;
  customerAddress?: string;
  distance?: number; // km
  estimatedMinutes?: number;
}

// Composant pour centrer automatiquement la carte
function MapBounds({
  customerLocation,
  driverLocation
}: {
  customerLocation: [number, number];
  driverLocation?: [number, number] | null;
}) {
  const map = useMap();

  useEffect(() => {
    const bounds: [number, number][] = [customerLocation];

    if (driverLocation) {
      bounds.push(driverLocation);
    }

    if (bounds.length > 1) {
      map.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [100, 100] });
    } else {
      map.setView(customerLocation, 14);
    }
  }, [customerLocation, driverLocation, map]);

  return null;
}

export default function DeliveryTrackingMap({
  customerLocation,
  driverLocation,
  customerAddress,
  distance,
  estimatedMinutes,
}: DeliveryTrackingMapProps) {
  const mapRef = useRef<L.Map | null>(null);

  // Ligne entre le livreur et le client (si les deux positions sont connues)
  const connectionLine: [number, number][] =
    driverLocation && customerLocation
      ? [driverLocation, customerLocation]
      : [];

  // `isolate` : sans contexte d'empilement, les calques de Leaflet (z-index
  // jusqu'à 1000) passent devant tout ce qui est fixé ou collant sur la page.
  return (
    <div className="w-full h-full relative isolate bg-gray-100 rounded-xl overflow-hidden">
      <MapContainer
        center={customerLocation}
        zoom={14}
        className="w-full h-full"
        zoomControl={true}
        style={{ minHeight: '400px', height: '100%' }}
        ref={(ref) => {
          if (ref) {
            mapRef.current = ref;
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Marqueur de la position du client (destination) */}
        <Marker position={customerLocation} icon={customerIcon}>
          <Popup>
            <div className="text-center">
              <div className="text-lg font-bold">🏠 Votre adresse</div>
              {customerAddress && (
                <div className="text-sm text-gray-600 mt-1">{customerAddress}</div>
              )}
            </div>
          </Popup>
        </Marker>

        {/* Marqueur de la position du livreur (si disponible) */}
        {driverLocation && (
          <Marker position={driverLocation} icon={driverIconAnimated}>
            <Popup>
              <div className="text-center">
                <div className="text-lg font-bold">🚚 Votre livreur</div>
                {distance !== undefined && (
                  <div className="text-sm text-gray-600 mt-1">
                    À {distance.toFixed(1)} km
                  </div>
                )}
                {estimatedMinutes !== undefined && (
                  <div className="text-sm font-bold text-blue-600">
                    Arrivée estimée : {estimatedMinutes} min
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        )}

        {/* Ligne de connexion entre le livreur et le client */}
        {connectionLine.length > 0 && (
          <Polyline
            positions={connectionLine}
            pathOptions={{
              color: '#3B82F6',
              weight: 3,
              opacity: 0.6,
              dashArray: '10, 10',
            }}
          />
        )}

        {/* Ajustement automatique des bounds */}
        <MapBounds
          customerLocation={customerLocation}
          driverLocation={driverLocation}
        />
      </MapContainer>

      {/* Indicateur de statut en overlay */}
      {!driverLocation && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white px-6 py-3 rounded-full shadow-lg font-bold z-[1000]">
          ⏳ En attente du départ du livreur...
        </div>
      )}

      {driverLocation && distance !== undefined && estimatedMinutes !== undefined && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg z-[1000]">
          <div className="flex items-center gap-3">
            <div className="text-2xl animate-bounce">🚚</div>
            <div>
              <div className="font-bold text-lg">
                Arrive dans ~{estimatedMinutes} min
              </div>
              <div className="text-sm opacity-90">
                Distance : {distance.toFixed(1)} km
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Style CSS pour animer le marqueur du livreur */}
      <style>{`
        .driver-marker-animated {
          animation: bounce-marker 2s ease-in-out infinite;
        }

        @keyframes bounce-marker {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  );
}
